import { useQuery } from "@tanstack/react-query";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";
import { FaqList, FaqSchema } from "@/components/SeoContent";
import { api } from "@/lib/api";
import { ShieldAlert, Target } from "lucide-react";

const localTodayDMY = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

function BannerArt() {
  return (
    <svg
      viewBox="0 0 400 160"
      className="absolute inset-0 w-full h-full"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="skyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#B7E1F0" />
          <stop offset="100%" stopColor="#E8F1E7" />
        </linearGradient>
      </defs>
      <rect width="400" height="160" fill="url(#skyG)" />
      {/* sun */}
      <circle cx="80" cy="55" r="26" fill="#FFE082" opacity="0.9" />
      {/* mountains */}
      <path d="M0 130 L60 80 L110 110 L170 60 L230 100 L290 70 L360 110 L400 90 L400 160 L0 160 Z" fill="#8FBEA2" opacity="0.85" />
      <path d="M0 150 L50 110 L110 130 L180 95 L240 120 L300 100 L360 130 L400 115 L400 160 L0 160 Z" fill="#4E8C6A" />
      {/* target on right - pushed further right and smaller */}
      <g transform="translate(360,45)">
        <circle cx="0" cy="0" r="26" fill="#fff" stroke="#5A3A19" strokeWidth="2" />
        <circle cx="0" cy="0" r="20" fill="#111" />
        <circle cx="0" cy="0" r="15" fill="#2C7CD1" />
        <circle cx="0" cy="0" r="10" fill="#E23B2B" />
        <circle cx="0" cy="0" r="5" fill="#F5D34E" />
      </g>
    </svg>
  );
}

export default function CommonNumbers() {
  const { data: todayData } = useQuery({
    queryKey: ["site-today"],
    queryFn: async () => (await api.get("/site/today")).data,
    staleTime: 60 * 60 * 1000,
  });
  const { data: cn } = useQuery({
    queryKey: ["common-today"],
    queryFn: async () => (await api.get("/common-numbers/today")).data,
    staleTime: 30_000,
  });

  const today = todayData?.date || localTodayDMY();
  const published = !!cn?.published;
  const data = cn?.data;

  return (
    <>
      <Seo
        title="Juwai Morning Teer Common Numbers Today"
        description="Reference common numbers for Juwai Morning Teer for today's date, updated manually by admin. For informational purposes only — no guarantees."
        path="/common-numbers"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Common Numbers", path: "/common-numbers" },
        ]}
      />
      <PageShell>
        {/* ===== Banner ===== */}
        <section className="relative rounded-2xl overflow-hidden h-40 sm:h-48 border border-black/10 shadow-[0_10px_24px_-14px_rgba(0,0,0,0.20)]">
          <BannerArt />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 max-w-[85%] mx-auto">
            <h1
              className="font-display font-black text-black text-2xl sm:text-4xl leading-[1.05] tracking-tight drop-shadow-[0_2px_0_rgba(255,255,255,0.6)]"
              data-testid="cn-h1"
            >
              Juwai Morning Teer<br />Common Number
            </h1>
            <p className="mt-1 text-slate-700 text-xs sm:text-sm font-semibold">
              Check today's reference target numbers
            </p>
          </div>
        </section>

        {/* ===== Date + Board card ===== */}
        <section className="mt-5">
          <div
            className="rounded-2xl overflow-hidden border-2 border-[#F04E1E] bg-white shadow-[0_10px_24px_-14px_rgba(0,0,0,0.20)]"
            data-testid="cn-card"
          >
            {/* orange DATE bar */}
            <div className="bg-[#F04E1E] py-3 px-4 text-center">
              <span
                className="font-display font-black text-white text-lg sm:text-xl tracking-wide"
                data-testid="cn-today-date"
              >
                DATE: {today}
              </span>
            </div>

            {!published && (
              <div className="p-8 text-center text-slate-500 text-sm" data-testid="cn-empty">
                No common numbers have been published for today. Please check
                back later.
              </div>
            )}

            {published && data && (
              <div className="p-2 sm:p-3">
                {/* F/R block */}
                <div className="grid grid-cols-3">
                  <Head>Direct</Head>
                  <Head>House</Head>
                  <Head>Ending</Head>
                  <Cell v={data.fr_direct} />
                  <Cell v={data.fr_house} />
                  <Cell v={data.fr_ending} />
                </div>
                {/* S/R block */}
                <div className="grid grid-cols-3 mt-2">
                  <Head>Direct</Head>
                  <Head>House</Head>
                  <Head>Ending</Head>
                  <Cell v={data.sr_direct} />
                  <Cell v={data.sr_house} />
                  <Cell v={data.sr_ending} />
                </div>
                {data.note && (
                  <p className="mt-3 px-2 text-sm text-slate-700 leading-relaxed" data-testid="cn-note">
                    {data.note}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ===== Disclaimer ===== */}
        <section className="mt-5">
          <div className="rounded-2xl border border-amber-300/60 bg-amber-50 p-4">
            <h3 className="font-display font-black text-amber-900 text-center text-lg">
              Disclaimer
            </h3>
            <p className="mt-1 text-sm text-amber-900/90 text-center leading-relaxed">
              These common numbers are provided for informational and reference
              purposes only. No guarantees are made regarding accuracy.
            </p>
          </div>
        </section>

        {/* ===== Info block ===== */}
        <section className="mt-6 rounded-2xl bg-white border border-black/5 p-5">
          <h2 className="font-display font-black text-black text-xl flex items-center gap-2">
            <Target className="w-5 h-5 text-[#F04E1E]" /> Common Number Reference
          </h2>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            The common number board is a set of reference figures (Direct, House
            and Ending) that some Teer followers track alongside the daily F/R
            (10:30 AM) and S/R (11:30 AM) results. On this website the numbers
            are added and updated manually by our team every day — nothing is
            generated automatically.
          </p>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            These figures are provided for informational and educational
            reference only. This website does not operate the underlying
            activity, sell tickets, or facilitate any wagering.
          </p>
        </section>

        {/* ===== Long-form SEO content ===== */}
        <section
          className="mt-6 rounded-2xl bg-white border border-black/5 p-5 sm:p-6"
          data-testid="cn-seo-content"
        >
          <h2 className="font-display font-black text-black text-2xl">
            Juwai Morning Teer Common Number Today
          </h2>
          <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
            On this page you can view today's{" "}
            <strong>Juwai Morning Teer Common Number</strong> for both the
            First Round (F/R, ~10:30 AM) and the Second Round (S/R, ~11:30 AM).
            Each round is broken down into three reference categories —{" "}
            <strong>Direct</strong>, <strong>House</strong> and{" "}
            <strong>Ending</strong>. These are simply reference numbers our
            team publishes each morning and are not predictions or guaranteed
            outcomes.
          </p>
          <h3 className="font-display font-black text-black text-lg mt-5">
            What "Direct", "House" and "Ending" mean
          </h3>
          <ul className="mt-2 space-y-2 text-slate-700 text-[15px] list-disc pl-5">
            <li>
              <strong>Direct</strong> — a full two-digit reference number
              (00–99) noted for the round.
            </li>
            <li>
              <strong>House</strong> — the reference tens digit derived from
              the direct number.
            </li>
            <li>
              <strong>Ending</strong> — the reference units digit derived from
              the direct number.
            </li>
          </ul>
          <h3 className="font-display font-black text-black text-lg mt-5">
            How the common numbers are prepared
          </h3>
          <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">
            Our editorial team looks at recent published results and standard
            references, and manually posts the day's common number board every
            morning. We do not use any algorithmic prediction, and we do not
            offer any paid subscription — the board is publicly visible for
            free reference.
          </p>
          <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">
            You can also cross-check the full{" "}
            <a href="/previous-results" className="text-[#F04E1E] font-semibold">
              Previous Results
            </a>{" "}
            archive to see how past dates were recorded.
          </p>
        </section>

        {/* ===== FAQ ===== */}
        <section className="mt-6" data-testid="cn-faq">
          <h2 className="font-display font-black text-black text-2xl px-1">
            Common Number — FAQs
          </h2>
          <FaqList
            testIdPrefix="cn-faq"
            faqs={[
              {
                q: "Are Juwai Morning Teer common numbers 100% accurate?",
                a: "No. Common numbers are informational reference figures only. They are neither official predictions nor a guarantee of any outcome.",
              },
              {
                q: "How often is the common number updated?",
                a: "Once per day, published manually by our team every morning before the first round.",
              },
              {
                q: "Do you charge any fee to view the common number?",
                a: "No. The page is completely free to view for informational and educational reference.",
              },
              {
                q: "Where can I see previous common numbers?",
                a: "Historical common numbers are visible on this site as they are posted daily. We do not backfill fake historical data.",
              },
            ]}
          />
          <FaqSchema
            id="common-numbers"
            faqs={[
              { q: "Are Juwai Morning Teer common numbers 100% accurate?", a: "No, they are informational reference only." },
              { q: "How often is the common number updated?", a: "Once per day, published manually every morning." },
              { q: "Do you charge any fee?", a: "No, the page is free to view." },
              { q: "Where can I see previous common numbers?", a: "As they are posted daily on this page." },
            ]}
          />
        </section>
      </PageShell>
    </>
  );
}

const Head = ({ children }) => (
  <div className="bg-neutral-900 text-white text-center font-display font-black text-sm sm:text-base py-2.5 border border-neutral-900">
    {children}
  </div>
);

const Cell = ({ v }) => (
  <div className="num-font text-center text-2xl sm:text-3xl font-black text-black py-3 bg-white border border-neutral-200">
    {v || "—"}
  </div>
);
